import { setViewModel } from './searchSlice';
import { setState as setAppState } from '../appState/appStateSlice';
import { AnyAction, unwrapResult } from '@reduxjs/toolkit';
import { Dispatch } from 'react';
import i18n from '../../i18n/config';

import { NavigateParams } from '../../components/BtNavigate';
import { RootState, store } from '../../app/store';
import { InputParams } from '../../components/BtInput';
import { DateParams } from '../../components/BtDate';
import { ButtonParams } from '../../components/BtButton';
import { apiPost, Failure, getGeocodeGoogle } from '../../support/apiCallHelper';
import { validateDate, validateLocation } from '../landing/validators';
import { GOOGLE_GEOCODE } from '../../support/apiConstants';
import { FailedParams } from '../../components/BtFailed';
import { SpinnerParams } from '../../components/BtSpinner';
import { LoadingParams } from '../../components/BtLoading';
import { createGatSelfServe } from '../gat/gatApi';
import { parseAddress } from '../../support/helpers';
import { setMissionData } from '../gat/gatSessionSlice';

export function none(logoUrl: string): SearchScreen.None {
  return {
    type: 'none',
    texts: {
      title: '',
      back: '',
      home: '',
      search: '',
      from: '',
      to: '',
      date: '',
      info: '',
      placeholderDate: '',
      placeholderAddress: '',
      body: '',
      pickUp: '',
      book: '',
      image: '',
      errorInfo: '',
      metaTitle: '',
      interrupt: '',
      loading: '',
      phone: '',
    },
    state: {
      from: {
        type: 'from',
        address: '',
        lat: '',
        lng: '',
      },
      to: {
        type: 'to',
        address: '',
        lat: '',
        lng: '',
      },
      date: '',
      slots: [],
      scroll: false,
    },
    error: {
      status: '',
      body: '',
    },
    loading: {
      title: '',
      navigation: {
        back: { title: '', image: 'ico_back_arrow', visible: false },
        home: { title: '', image: 'ico_home', visible: false },
        image: logoUrl,
      },
    },
  };
}

export namespace SearchScreen {
  interface Texts {
    title: string;
    back: string;
    home: string;
    search: string;
    from: string;
    to: string;
    date: string;
    info: string;
    placeholderDate: string;
    placeholderAddress: string;
    body: string;
    pickUp: string;
    book: string;
    image: string;
    errorInfo: string;
    metaTitle: string;
    interrupt: string;
    loading: string;
    phone: string;
  }

  export interface State {
    from: Address;
    to: Address;
    date: string;
    slots: Slot[];
    scroll: boolean;
  }

  export interface Address {
    type: searchType;
    address: string;
    lat: string;
    lng: string;
    zipCode?: string;
    city?: string;
  }

  export interface Slot {
    slotTime: string;
    startTime: string;
    endTime: string;
    price: string;
  }

  export type searchType = 'to' | 'from';

  export interface None {
    type: 'none';
    texts: Texts;
    state: State;
    error: FailedParams;
    loading: LoadingParams;
  }

  export interface Loading {
    type: 'loading';
    texts: Texts;
    state: State;
    loading: LoadingParams;
  }

  export interface Ready {
    type: 'ready';
    texts: Texts;
    state: State;
    greeting: string;
    search: ButtonParams;
    navigation: NavigateParams;
    from: InputParams;
    to: InputParams;
    date: DateParams;
    info: string;
    image: string;
    body: string;
    pickUp: string;
    book: ButtonParams;
    time: string;
    price: string;
    metaTitle: string;
    spinner: SpinnerParams;
    ikon: string;
    phone: string;
  }

  export interface Failed {
    type: 'failed';
    texts: Texts;
    state: State;
    error: FailedParams;
  }

  export type ViewModel = None | Loading | Ready | Failed;

  export function isNone(viewModel: ViewModel): viewModel is None {
    return (viewModel as None).type === 'none';
  }
  export function isLoading(viewModel: ViewModel): viewModel is Loading {
    return (viewModel as Loading).type === 'loading';
  }
  export function isReady(viewModel: ViewModel): viewModel is Ready {
    return (viewModel as Ready).type === 'ready';
  }
  export function isFailed(viewModel: ViewModel): viewModel is Failed {
    return (viewModel as Failed).type === 'failed';
  }

  function getTexts(viewModel: ViewModel): Texts {
    if (isNone(viewModel)) return viewModel.texts;
    if (isLoading(viewModel)) return viewModel.texts;
    if (isReady(viewModel)) return viewModel.texts;
    if (isFailed(viewModel)) return viewModel.texts;
    return {
      title: '',
      home: '',
      back: '',
      search: '',
      from: '',
      to: '',
      date: '',
      info: '',
      placeholderAddress: '',
      placeholderDate: '',
      body: '',
      pickUp: '',
      book: '',
      image: '',
      errorInfo: '',
      metaTitle: '',
      interrupt: '',
      loading: '',
      phone: '',
    };
  }

  function loading(viewModel: ViewModel, state: State, logoUrl: string): Loading {
    const t = i18n.t;

    return {
      type: 'loading',
      texts: {
        title: t('searchScreen.title'),
        back: t('searchScreen.back'),
        home: t('searchScreen.home'),
        search: t('searchScreen.search'),
        from: t('searchScreen.from'),
        to: t('searchScreen.to'),
        date: t('searchScreen.date'),
        info: t('searchScreen.info'),
        placeholderDate: t('searchScreen.placeholderDate'),
        placeholderAddress: t('searchScreen.placeholderAddress'),
        body: t('searchScreen.body'),
        pickUp: t('searchScreen.pickUp'),
        book: t('searchScreen.book'),
        image: t('image.ico_offer'),
        errorInfo: t('searchScreen.errorInfo'),
        metaTitle: t('searchScreen.metaTitle'),
        interrupt: t('searchScreen.interrupt'),
        loading: t('loading.title'),
        phone: t('supportScreen.phone'),
      },
      loading: {
        title: t('loading.title'),
        navigation: {
          back: { title: '', image: 'ico_back_arrow', visible: false },
          home: { title: '', image: 'ico_home', visible: false },
          image: logoUrl,
        },
      },
      state: state,
    };
  }

  function failed(
    viewModel: ViewModel,
    texts: Texts,
    state: State,
    body: string,
    status: string,
  ): Failed {
    return {
      type: 'failed',
      texts: texts,
      state: state,
      error: {
        status: status,
        body: body,
      },
    };
  }

  function ready(
    viewModel: ViewModel,
    state: State,
    texts: Texts,
    errorFrom: string | null,
    errorTo: string | null,
    errorDate: string | null,
    logoUrl: string,
  ): Ready {
    console.log('state', state);
    return {
      type: 'ready',
      texts: texts,
      greeting: texts.title,
      search: { title: texts.search, image: null, visible: true },
      navigation: {
        back: { title: '', image: 'ico_back_arrow', visible: true },
        home: { title: '', image: 'ico_home', visible: true },
        image: logoUrl,
      },
      from: {
        id: state.from.type,
        name: state.from.type,
        title: texts.from,
        message: errorFrom ? errorFrom : '',
        disabled: false,
        value: state.from.address,
        placeholder: texts.placeholderAddress,
        addOnLeft: 'ico_position',
        type: 'text',
      },
      to: {
        id: state.to.type,
        name: state.to.type,
        title: texts.to,
        message: errorTo ? errorTo : '',
        disabled: false,
        value: state.to.address,
        placeholder: texts.placeholderAddress,
        addOnLeft: 'ico_position',
        type: 'text',
      },
      date: {
        id: 'date',
        name: 'date',
        title: texts.date,
        message: errorDate ? errorDate : '',
        disabled: false,
        value: state.date,
        placeholder: texts.placeholderDate,
      },
      info: texts.info,
      image: texts.image,
      body: texts.body,
      pickUp: texts.pickUp,
      book: { title: texts.book, image: null, visible: true },
      time: 'ico_time',
      price: 'ico_cost',
      state: state,
      metaTitle: texts.metaTitle,
      spinner: { interrupt: { title: texts.interrupt, image: '', visible: true } },
      ikon: 'ico_support',
      phone: texts.phone,
    };
  }

  export function start(
    viewModel: ViewModel,
    logoUrl: string,
  ): (dispatch: Dispatch<AnyAction>) => void {
    const t = i18n.t;
    const appState = store.getState().appState.state;
    if (!appState.sessionActive) {
      return (dispatch) => {
        dispatch(
          setViewModel(
            failed(viewModel, viewModel.texts, viewModel.state, t('failed.session'), ''),
          ),
        );
      };
    }

    return (dispatch) => {
      dispatch(setViewModel(loading(viewModel, viewModel.state, logoUrl)));
    };
  }

  export async function googlePlaceDetails(targetType: string): Promise<{
    fullAddress: string;
    address: string;
    zipCode: string;
    city: string;
  }> {
    const options = {
      fields: ['address_components'],
      strictBounds: true,
      types: ['address'],
    };
    const autocomplete = new google.maps.places.Autocomplete(
      document.getElementById(targetType) as HTMLInputElement,
      options,
    );
    return new Promise((resolve, reject) => {
      autocomplete.addListener('place_changed', () => {
        const place = autocomplete.getPlace();
        const placeObj = {
          address: '',
          streetNumber: '',
          zipCode: '',
          city: '',
        };

        for (const component of place.address_components as google.maps.GeocoderAddressComponent[]) {
          console.log('Component:', {
            long_name: component.long_name,
            short_name: component.short_name,
            types: component.types,
          });
          const componentType = component.types[0];
          switch (componentType) {
            case 'route':
            case 'street_address':
              placeObj.address = component.long_name;
              break;

            case 'street_number':
              placeObj.streetNumber = component.long_name;
              break;

            case 'postal_code':
              placeObj.zipCode = component.long_name;
              break;

            case 'locality':
            case 'postal_town':
              placeObj.city = component.long_name;
              break;
          }
        }

        const fullStreet = `${placeObj.address} ${placeObj.streetNumber}`.trim();
        const fullAddress = `${fullStreet}, ${placeObj.zipCode} ${placeObj.city}`.trim();

        if (placeObj.address && placeObj.zipCode && placeObj.city) {
          resolve({
            fullAddress,
            address: fullStreet,
            zipCode: placeObj.zipCode,
            city: placeObj.city,
          });
        } else {
          reject();
        }
      });
    });
  }

  export async function saveGoogleAddress(
    viewModel: ViewModel,
    targetType: string,
    logoUrl: string,
  ): Promise<(dispatch: Dispatch<AnyAction>) => void> {
    const { fullAddress, zipCode, city } = await googlePlaceDetails(targetType);
    const response = await geocodeAdress(fullAddress, targetType as SearchScreen.searchType);

    let lat = '';
    let lng = '';
    if (response instanceof Failure === false) {
      const res = response as Address[];
      if (res.length > 0) {
        lat = res[0].lat;
        lng = res[0].lng;
      }
    }

    if (response instanceof Failure) {
      const res = response as Failure;
      return async (dispatch) => {
        dispatch(
          await setViewModel(
            failed(
              viewModel,
              viewModel.texts,
              viewModel.state,
              res.status.toString(),
              res.statusText,
            ),
          ),
        );
      };
    } else {
      let newState: State;
      if ((targetType as SearchScreen.searchType) === 'from') {
        newState = {
          ...viewModel.state,
          from: {
            address: fullAddress,
            type: 'from',
            zipCode: zipCode,
            city: city,
            lat: lat,
            lng: lng,
          },
          to: {
            address: viewModel.state.to.address,
            zipCode: viewModel.state.to.zipCode,
            city: viewModel.state.to.city,
            type: 'to',
            lat: viewModel.state.to.lat,
            lng: viewModel.state.to.lng,
          },
          date: viewModel.state.date,
          slots: [],
          scroll: false,
        };
      } else {
        newState = {
          ...viewModel.state,
          from: {
            address: viewModel.state.from.address,
            zipCode: viewModel.state.from.zipCode,
            city: viewModel.state.from.city,
            type: 'from',
            lat: viewModel.state.from.lat,
            lng: viewModel.state.from.lng,
          },
          to: {
            address: fullAddress,
            zipCode: zipCode,
            city: city,
            type: 'to',
            lat: lat,
            lng: lng,
          },
          date: viewModel.state.date,
          slots: [],
          scroll: false,
        };
      }

      const texts = {
        ...viewModel.texts,
        info: viewModel.texts.info,
        image: viewModel.texts.image,
      };

      return async (dispatch) => {
        dispatch(await setViewModel(ready(viewModel, newState, texts, null, null, null, logoUrl)));
      };
    }
  }

  export async function uppdateState(
    viewModel: ViewModel,
    event: React.ChangeEvent<HTMLInputElement>,
    logoUrl: string,
  ): Promise<(dispatch: Dispatch<AnyAction>) => void> {
    const texts = { ...viewModel.texts, info: viewModel.texts.info, image: viewModel.texts.image };

    let newState: State;
    if ((event.target.name as SearchScreen.searchType) === 'from') {
      newState = {
        ...viewModel.state,
        from: { address: event.target.value, type: 'from', lat: '', lng: '' },
        to: {
          address: viewModel.state.to.address,
          zipCode: viewModel.state.to.zipCode,
          city: viewModel.state.to.city,
          type: 'to',
          lat: viewModel.state.to.lat,
          lng: viewModel.state.to.lng,
        },
        date: viewModel.state.date,
        slots: [],
        scroll: false,
      };
    } else {
      newState = {
        ...viewModel.state,
        from: {
          address: viewModel.state.from.address,
          zipCode: viewModel.state.from.zipCode,
          city: viewModel.state.from.city,
          type: 'from',
          lat: viewModel.state.from.lat,
          lng: viewModel.state.from.lng,
        },
        to: { address: event.target.value, type: 'to', lat: '', lng: '' },
        date: viewModel.state.date,
        slots: [],
        scroll: false,
      };
    }

    return async (dispatch) => {
      dispatch(await setViewModel(ready(viewModel, newState, texts, null, null, null, logoUrl)));
    };
  }

  export async function uppdateDate(
    viewModel: ViewModel,
    event: React.ChangeEvent<HTMLInputElement>,
    logoUrl: string,
  ): Promise<(dispatch: Dispatch<AnyAction>) => void> {
    const target = event.target;
    const value = target.value;
    const dateError = validateDate(value);

    const newState = { ...viewModel.state, date: value, slots: [], scroll: false };
    const texts = { ...viewModel.texts, info: viewModel.texts.info, image: viewModel.texts.image };

    if (dateError) {
      return (dispatch) => {
        dispatch(
          setViewModel(ready(viewModel, newState, viewModel.texts, null, null, dateError, logoUrl)),
        );
      };
    } else {
      return async (dispatch) => {
        dispatch(await setViewModel(ready(viewModel, newState, texts, null, null, null, logoUrl)));
      };
    }
  }

  export async function load(
    viewModel: ViewModel,
    logoUrl: string,
  ): Promise<(dispatch: Dispatch<AnyAction>) => void> {
    const appState = store.getState().appState.state;

    const street = appState.address ?? '';
    const zipCode = appState.zipCode ?? '';
    const city = appState.city ?? '';

    const fullAddress =
      appState.sourceAddress !== ''
        ? appState.sourceAddress
        : `${street}, ${zipCode} ${city}`.trim();

    let lat = '';
    let lng = '';

    if (fullAddress !== '') {
      const response = await geocodeAdress(fullAddress, 'from');

      if (!(response instanceof Failure)) {
        const res = response as Address[];
        if (res.length > 0) {
          lat = res[0].lat;
          lng = res[0].lng;
        }
      }
    }

    const newState: State = {
      ...viewModel.state,
      from: {
        address: fullAddress,
        zipCode,
        city,
        type: 'from',
        lat,
        lng,
      },
      slots: [],
    };

    return (dispatch) => {
      dispatch(
        setViewModel(ready(viewModel, newState, viewModel.texts, null, null, null, logoUrl)),
      );
    };
  }

  const formatTimeToHours = (isoString: string): string => {
    const date = new Date(isoString);
    const hours = date.getUTCHours().toString().padStart(2, '0');
    const minutes = date.getUTCMinutes().toString().padStart(2, '0');
    return `${hours}:${minutes}`;
  };

  export async function book(
    viewModel: ViewModel,
    price: string,
    time: string,
  ): Promise<(dispatch: Dispatch<AnyAction>) => void> {
    return (dispatch) => {
      dispatch(
        setAppState({
          ...store.getState().appState.state,
          sourceLatitude: viewModel.state.from.lat,
          sourceLongitude: viewModel.state.from.lng,
          sourceAddress: viewModel.state.from.address,
          destinationLatitude: viewModel.state.to.lat,
          destinationLongitude: viewModel.state.to.lng,
          destinationAddress: viewModel.state.to.address,
          missionDate: time,
          missionTime: formatTimeToHours(time),
          missionPrice: price,
        }),
      );
    };
  }

  export async function search(
    viewModel: ViewModel,
    logoUrl: string,
  ): Promise<(dispatch: Dispatch<AnyAction>) => void> {
    const t = i18n.t;
    const dateError = validateDate(viewModel.state.date);
    const fromError = validateLocation(
      viewModel.state.from.address,
      viewModel.state.from.lat,
      viewModel.state.from.lng,
    );
    const toError = validateLocation(
      viewModel.state.to.address,
      viewModel.state.to.lat,
      viewModel.state.to.lng,
    );

    if (dateError || fromError || toError) {
      return (dispatch) => {
        dispatch(
          setViewModel(
            ready(
              viewModel,
              { ...viewModel.state, scroll: false },
              viewModel.texts,
              fromError,
              toError,
              dateError,
              logoUrl,
            ),
          ),
        );
      };
    } else {
      const slotsFromGat = await callGat(viewModel.state);
      const newState = { ...viewModel.state, slots: slotsFromGat, scroll: true };

      if (slotsFromGat.length > 0) {
        return (dispatch) => {
          dispatch(
            setViewModel(ready(viewModel, newState, viewModel.texts, null, null, null, logoUrl)),
          );
        };
      } else {
        return (dispatch) => {
          dispatch(
            setViewModel(
              ready(
                viewModel,
                newState,
                {
                  ...viewModel.texts,
                  info: viewModel.texts.errorInfo,
                  image: t('image.ico_location_no_info'),
                },
                null,
                null,
                null,
                logoUrl,
              ),
            ),
          );
        };
      }
    }
  }

  export async function reset(logoUrl: string): Promise<(dispatch: Dispatch<AnyAction>) => void> {
    return (dispatch) => {
      dispatch(setViewModel(none(logoUrl)));
    };
  }

  let controller: AbortController | null = null;
  export async function cancelFetch() {
    if (controller) {
      controller.abort();
    }
  }

  interface GatResult {
    destination: {
      latitude: string;
      longitude: string;
    };
    price: string;
    slots: [
      {
        fromTime: string;
        timezone: string;
        toTime: string;
      },
    ];
    source: { latitude: string; longitude: string };
  }

  function formatTime(time: string): string {
    const [date, timePart] = time.split('T');
    const [hours, minutes] = timePart.split(':');
    return `${date} ${hours}.${minutes}`;
  }

  export async function callGat(state: State): Promise<Slot[]> {
    const appState = store.getState().appState.state;
    controller = new AbortController();
    const suggestions: Slot[] = [];

    const { missionId, token } = store.getState().gatSession;

    try {
      const missionData = {
        customerCode: appState.isActive === 'person' ? appState.govId : appState.govNo,
        registrationNumber: appState.regno,
        missionId: missionId,
        token: token,
        customerType: appState.isActive.charAt(0).toUpperCase() + appState.isActive.slice(1),
        missionDate: new Date(state.date).toISOString(),
        source: {
          latitude: parseFloat(state.from.lat),
          longitude: parseFloat(state.from.lng),
          city: state.from.city,
          zipCode: state.from.zipCode,
          address: state.from.address,
        },
        destination: {
          latitude: parseFloat(state.to.lat),
          longitude: parseFloat(state.to.lng),
          city: state.to.city,
          zipCode: state.to.zipCode,
          address: state.to.address,
        },
      };

      const selfServeSlotTimes = await store.dispatch(createGatSelfServe(missionData));

      store.dispatch(setMissionData(missionData));
      const result = unwrapResult(selfServeSlotTimes);

      if (result?.timeSlots?.length) {
        result.timeSlots.forEach((slot) => {
          const order = result?.orders[0];
          const total = order?.total ?? 0;
          const totalVAT = order?.calculatedVAT ?? 0;

          const totalWithVAT = total + totalVAT;
          const isCompany = appState.isActive === 'company';

          suggestions.push({
            slotTime: slot.start,
            startTime: formatTime(slot.start),
            endTime: formatTime(slot.end),
            price: isCompany ? `${total} kr` : `${totalWithVAT} kr`,
          });
        });
      }

      return suggestions;
    } catch (e) {
      // eslint-disable-next-line no-console
      console.log('GAT ' + e);

      return suggestions;
    }
  }

  interface GoogleResult {
    results: [
      {
        types: [];
        formatted_address: string;
        address_components: [
          {
            short_name: string;
            long_name: string;
            postcode_localities: [string];
            types: [string];
          },
        ];
        partial_match: boolean;
        place_id: string;
        postcode_localities: [string];
        geometry: {
          location: { lat: string; lng: string };
        };
      },
    ];
    status: string;
  }

  export async function geocodeAdress(
    address: string,
    targetType: SearchScreen.searchType,
  ): Promise<Address[] | Failure> {
    const geocode = `address=${address}`;
    const suggestions: Address[] = [];
    try {
      const callGoogle = await getGeocodeGoogle<GoogleResult>(GOOGLE_GEOCODE, geocode);
      if (callGoogle instanceof Failure === false) {
        const result = callGoogle as GoogleResult;
        if (result.status === 'OK') {
          result.results.forEach((element) => {
            suggestions.push({
              type: targetType,
              address: element.formatted_address,
              lat: element.geometry.location.lat,
              lng: element.geometry.location.lng,
            });
          });
        }

        return suggestions;
      } else {
        return callGoogle as Failure;
      }
    } catch (e) {
      // eslint-disable-next-line no-console
      console.log('Reject google ' + e);
      return e as Failure;
    }
  }
}
